import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Collapse,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material';
import { useNotify, useGetIdentity } from 'react-admin';

interface Department {
    id?: number;
    name: string;
    company: string;
    users?: User[];
}

interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
}

const DepartmentManagement: React.FC = () => {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
    const [formData, setFormData] = useState<Department>({ name: '', company: '' });
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [loadingUsers, setLoadingUsers] = useState<Set<number>>(new Set());
    const notify = useNotify();
    const { data: identity } = useGetIdentity();

    const companyId = (identity as any)?.companyId;

    useEffect(() => {
        console.log('[DepartmentManagement] Identity:', identity);
        console.log('[DepartmentManagement] CompanyId:', companyId);
        if (companyId) {
            loadDepartments();
        } else {
            setLoading(false);
        }
    }, [companyId]);

    const loadDepartments = async () => {
        if (!companyId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/departments?company.id=${companyId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log('[DepartmentManagement] API Response:', data);
                console.log('[DepartmentManagement] Departments:', data.member || data['hydra:member'] || []);
                setDepartments(data.member || data['hydra:member'] || []);
            } else {
                console.error('[DepartmentManagement] API Error:', response.status, response.statusText);
                const errorText = await response.text();
                console.error('[DepartmentManagement] Error details:', errorText);
                notify('Fehler beim Laden der Abteilungen', { type: 'error' });
            }
        } catch (error) {
            console.error('Error loading departments:', error);
            notify('Fehler beim Laden der Abteilungen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (department?: Department) => {
        if (department) {
            setEditingDepartment(department);
            setFormData(department);
        } else {
            setEditingDepartment(null);
            setFormData({ name: '', company: `/api/companies/${companyId}` });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingDepartment(null);
        setFormData({ name: '', company: '' });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = editingDepartment
                ? `${process.env.REACT_APP_SSO_URL}/api/departments/${editingDepartment.id}`
                : `${process.env.REACT_APP_SSO_URL}/api/departments`;

            const method = editingDepartment ? 'PUT' : 'POST';

            const dataToSend = editingDepartment
                ? formData
                : { ...formData, company: `/api/companies/${companyId}` };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/ld+json',
                    'Accept': 'application/ld+json'
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                notify(
                    editingDepartment
                        ? 'Abteilung erfolgreich aktualisiert'
                        : 'Abteilung erfolgreich erstellt',
                    { type: 'success' }
                );
                handleCloseDialog();
                loadDepartments();
            } else {
                notify('Fehler beim Speichern der Abteilung', { type: 'error' });
            }
        } catch (error) {
            console.error('Error saving department:', error);
            notify('Fehler beim Speichern der Abteilung', { type: 'error' });
        }
    };

    const handleDelete = async (departmentId: number) => {
        if (!window.confirm('Möchten Sie diese Abteilung wirklich löschen?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/departments/${departmentId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                notify('Abteilung erfolgreich gelöscht', { type: 'success' });
                loadDepartments();
            } else {
                notify('Fehler beim Löschen der Abteilung', { type: 'error' });
            }
        } catch (error) {
            console.error('Error deleting department:', error);
            notify('Fehler beim Löschen der Abteilung', { type: 'error' });
        }
    };

    const toggleRow = async (departmentId: number) => {
        const newExpanded = new Set(expandedRows);

        if (expandedRows.has(departmentId)) {
            newExpanded.delete(departmentId);
            setExpandedRows(newExpanded);
        } else {
            newExpanded.add(departmentId);
            setExpandedRows(newExpanded);

            // Load users for this department if not already loaded
            const department = departments.find(d => d.id === departmentId);
            if (department && !department.users) {
                await loadDepartmentUsers(departmentId);
            }
        }
    };

    const loadDepartmentUsers = async (departmentId: number) => {
        setLoadingUsers(prev => new Set(prev).add(departmentId));

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/users?department.id=${departmentId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                const users = data.member || data['hydra:member'] || [];

                setDepartments(prev => prev.map(dept =>
                    dept.id === departmentId ? { ...dept, users } : dept
                ));
            }
        } catch (error) {
            console.error('Error loading department users:', error);
        } finally {
            setLoadingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(departmentId);
                return newSet;
            });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Abteilungen</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ bgcolor: '#e72956', '&:hover': { bgcolor: '#c21642' } }}
                >
                    Neue Abteilung
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="50"></TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {departments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    Keine Abteilungen vorhanden
                                </TableCell>
                            </TableRow>
                        ) : (
                            departments.map((department) => (
                                <React.Fragment key={department.id}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleRow(department.id!)}
                                            >
                                                {expandedRows.has(department.id!) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{department.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(department)}
                                                sx={{ color: '#1f3240' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(department.id!)}
                                                sx={{ color: '#e72956' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                                            <Collapse in={expandedRows.has(department.id!)} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 2 }}>
                                                    <Typography variant="h6" gutterBottom component="div">
                                                        Zugeordnete Benutzer
                                                    </Typography>
                                                    {loadingUsers.has(department.id!) ? (
                                                        <CircularProgress size={24} />
                                                    ) : department.users && department.users.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {department.users.map(user => (
                                                                <Chip
                                                                    key={user.id}
                                                                    label={`${user.firstName} ${user.lastName} (${user.email})`}
                                                                    size="small"
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Keine Benutzer zugeordnet
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingDepartment ? 'Abteilung bearbeiten' : 'Neue Abteilung'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!formData.name}
                        sx={{ bgcolor: '#e72956', '&:hover': { bgcolor: '#c21642' } }}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DepartmentManagement;
